import { appConfig } from 'appConfig';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import BottomSheet from 'components/BottomSheet';
import { setAsPrimaryEstimate } from 'queries/estimates';
import Button, { Size, Variants } from 'components/Button';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuth from 'hooks/useAuth';

type AppProps = {
  open: boolean;
  onClose: () => void;
  estimate: any;
  title?: string;
  description?: string;
  showConfirm?: boolean;
};

const SetAsPrimaryRestrict: React.FC<AppProps> = ({
  open,
  onClose,
  estimate,
  title = 'Set This Estimate as Primary?',
  description = 'You created multiple estimates for this job. Before moving forward, you need to set an estimate as primary. Would you like to set this estimate to primary?',
  showConfirm = true,
}) => {
  const { embedMode } = useAuth();
  const { jobId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setPrimaryMutation = useMutation(
    () => setAsPrimaryEstimate(estimate.id),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(['estimates']),
          queryClient.invalidateQueries(['job-detail', jobId]),
        ]);
        queryClient.invalidateQueries(['job-checklist', jobId]);
        queryClient.invalidateQueries(['matorders']);
        queryClient.invalidateQueries(['workorders']);
        if (embedMode && window.top) {
          window.top.location = `${appConfig.legacyUrl}/jobs/${jobId}/estimates/${estimate.id}`;
        } else {
          return navigate(`/jobs/${jobId}/estimates/${estimate.id}`);
        }
      },
      onSettled: () => {
        onClose();
      },
    }
  );

  return (
    <BottomSheet open={open} closeSheet={onClose} maxWidth="max-w-xl">
      <div>
        <div className="text-xl font-bold">{title}</div>
        <div className="mt-4">{description}</div>
        <div className="mt-6 flex justify-end space-x-2">
          <div>
            <Button
              size={Size.Medium}
              variant={Variants.Outline}
              label="View All"
              onClick={() => {
                navigate(`/jobs/${jobId}/estimates`);
                onClose();
              }}
              disabled={setPrimaryMutation.isLoading}
            />
          </div>
          {showConfirm && (
            <div>
              <Button
                size={Size.Medium}
                variant={Variants.Primary}
                label="Confirm"
                onClick={() => setPrimaryMutation.mutate()}
                loading={setPrimaryMutation.isLoading}
              />
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default SetAsPrimaryRestrict;
